/**
 * Anode Assignment Cards Module
 * Handles display and management of anode assignments
 */

import { openAnodeCatalogModal } from './anode-modal.js';

/**
 * Fetch inventory status for anodes
 * @param {string[]} anodeCatalogIds - Array of anode catalog IDs
 * @param {string} boatId - The boat UUID
 * @returns {Promise<Array>} Array of inventory status objects
 */
async function fetchAnodeInventoryStatus(anodeCatalogIds, boatId) {
  try {
    const response = await fetch(
      `${window.supabaseClient.supabaseUrl}/functions/v1/anode-inventory-status`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${window.supabaseClient.supabaseKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anode_catalog_ids: anodeCatalogIds,
          boat_id: boatId,
        }),
      }
    );

    if (!response.ok) {
      console.error('Failed to fetch inventory status:', response.statusText);
      return [];
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching inventory status:', error);
    return []; // Graceful degradation
  }
}

/**
 * Get inventory status for a specific anode catalog ID
 * @param {string} anodeCatalogId - The anode catalog ID
 * @returns {Object|null} Inventory status object or null if not found
 */
function getAnodeInventoryStatus(anodeCatalogId) {
  if (!window.__anodeInventoryStatus) return null;
  return window.__anodeInventoryStatus.find(
    status => status.anode_catalog_id === anodeCatalogId
  );
}

/**
 * Get suggested button action based on inventory status
 * @param {Object} inventoryStatus - The inventory status object
 * @returns {string|null} Suggested button action ('replaced', 'retrieve', 'order') or null
 */
function getSuggestedButton(inventoryStatus) {
  if (!inventoryStatus) return null;

  if (inventoryStatus.on_packing_list) {
    return 'replaced'; // Suggest replaced if on packing list
  } else if (inventoryStatus.quantity_available > 0) {
    return 'retrieve'; // Suggest retrieve if in stock
  } else {
    return 'order'; // Suggest order if out of stock
  }
}

/**
 * Render inventory badge HTML based on inventory status
 * @param {Object} inventoryStatus - The inventory status object
 * @returns {string} HTML string for inventory badge
 */
function renderInventoryBadge(inventoryStatus) {
  if (!inventoryStatus) return '';

  if (inventoryStatus.on_packing_list) {
    return '<span class="inventory-badge badge-on-packing-list">On packing list</span>';
  } else if (inventoryStatus.quantity_available > 0) {
    return `<span class="inventory-badge badge-in-stock">(${inventoryStatus.quantity_available} in stock)</span>`;
  } else {
    return '<span class="inventory-badge badge-out-of-stock">Out of Stock</span>';
  }
}

/**
 * Update the anode assignments list display
 */
export function updateAnodeAssignmentsList() {
    const listContainer = document.getElementById('anodeAssignmentsList');
    if (!listContainer) return;

    if (Object.keys(window.anodeAssignments).length === 0) {
        listContainer.innerHTML = '<p style="text-align: center; color: #666; padding: 20px;">No anode locations assigned yet. Click a location above to add one.</p>';
        return;
    }

    const entries = Object.entries(window.anodeAssignments);

    listContainer.innerHTML = entries.map(([key, anode]) => `
        <div class="anode-assignment-card" id="anode-card-${key}">
            <!-- Header: Location - Model - Buttons (Change/Remove) -->
            <div class="anode-assignment-header">
                <div class="anode-assignment-title" style="flex: 1;">
                    <span style="font-weight: 600;">${anode.location.charAt(0).toUpperCase() + anode.location.slice(1)} - ${anode.position}</span>
                    ${anode.anode_name ? `<span style="color: #666; font-weight: normal; margin-left: 4px;">- ${anode.anode_name}</span>` : ''}
                </div>
                <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                    ${anode.anode_name ? `<button class="anode-assignment-change" onclick="window.__openAnodeModalForCard('${key}', '${anode.location}', '${anode.position}')" style="padding: 4px 12px; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;">
                        Change Anode
                    </button>` : ''}
                    <!-- Remove from Charge (session only) - visible in edit mode -->
                    <button class="anode-assignment-remove anode-edit-mode-only"
                            onclick="window.__removeAnodeFromCharge('${key}')"
                            style="display: none; padding: 4px 12px; background: #f39c12; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;">
                        Remove from Charge
                    </button>
                    <!-- Delete Permanently (database) - visible in edit mode -->
                    <button class="anode-assignment-delete anode-edit-mode-only"
                            onclick="window.__deleteAnodePermanently('${key}', '${anode.location}', '${anode.position}')"
                            style="display: none; padding: 4px 12px; background: #e74c3c; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500;">
                        Delete Permanently
                    </button>
                </div>
            </div>

${anode.anode_name ? `
            <!-- Historical Condition Display -->
            <div class="anode-condition-historical" style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 13px; color: #666;">
                <div id="anode-historical-${key}">Last service: <span style="font-weight: 600;">N/A</span></div>
                <div style="text-align: right; font-weight: 600; color: #2c3e50;">This service: <span id="anode-current-${key}">${formatConditionValue(anode.condition)}</span></div>
            </div>

            <!-- Condition Slider -->
            <div style="margin: 10px 0 12px 0;">
                <div class="slider-container">
                    <input type="range" id="anode-slider-${key}" class="condition-slider anode-condition-slider"
                           min="0" max="10" value="${getConditionSliderValue(anode.condition)}" step="1"
                           oninput="window.__updateAnodeCondition('${key}', this.value)">
                    <div class="slider-labels" id="slider-labels-${key}">
                        <span data-value="0">100%</span>
                        <span data-value="1">90%</span>
                        <span data-value="2">80%</span>
                        <span data-value="3">70%</span>
                        <span data-value="4">60%</span>
                        <span data-value="5">50%</span>
                        <span data-value="6">40%</span>
                        <span data-value="7">30%</span>
                        <span data-value="8">20%</span>
                        <span data-value="9">10%</span>
                        <span data-value="10">Missing</span>
                    </div>
                </div>
            </div>

            <!-- Hidden condition value for internal tracking -->
            <span id="condition-display-${key}" style="display: none;">${formatConditionValue(anode.condition)}</span>

            ${(() => {
                // Get inventory status for this anode
                const inventoryStatus = getAnodeInventoryStatus(anode.anode_id);
                const suggestedButton = getSuggestedButton(inventoryStatus);
                const badgeHtml = renderInventoryBadge(inventoryStatus);

                // Render inventory badge section
                const inventoryBadgeSection = badgeHtml ? `
                    <div class="inventory-status-section" style="margin-bottom: 8px; text-align: center;">
                        ${badgeHtml}
                    </div>
                ` : '';

                // Determine button classes with 'suggested' class
                const replacedClass = `anode-status-btn ${anode.status === 'replaced' ? 'active' : ''} ${suggestedButton === 'replaced' ? 'suggested' : ''}`;
                const retrieveClass = `anode-status-btn ${anode.status === 'retrieve' ? 'active' : ''} ${suggestedButton === 'retrieve' ? 'suggested' : ''}`;
                const orderClass = `anode-status-btn ${anode.status === 'order' ? 'active' : ''} ${suggestedButton === 'order' ? 'suggested' : ''}`;

                return `
                    ${inventoryBadgeSection}
                    <!-- Status Buttons (centered, larger) -->
                    <div style="display: flex; gap: 8px; justify-content: center;">
                        <button class="${replacedClass}"
                                onclick="window.__updateAnodeStatus('${key}', 'replaced')"
                                style="padding: 10px 16px; border: 2px solid #00b894; background: ${anode.status === 'replaced' ? '#00b894' : 'white'}; color: ${anode.status === 'replaced' ? 'white' : '#00b894'}; border-radius: 5px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s;">
                            Replaced
                        </button>
                        <button class="${retrieveClass}"
                                onclick="window.__updateAnodeStatus('${key}', 'retrieve')"
                                style="padding: 10px 16px; border: 2px solid #3498db; background: ${anode.status === 'retrieve' ? '#3498db' : 'white'}; color: ${anode.status === 'retrieve' ? 'white' : '#3498db'}; border-radius: 5px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s;">
                            Retrieve
                        </button>
                        <button class="${orderClass}"
                                onclick="window.__updateAnodeStatus('${key}', 'order')"
                                style="padding: 10px 16px; border: 2px solid #fd7e14; background: ${anode.status === 'order' ? '#fd7e14' : 'white'}; color: ${anode.status === 'order' ? 'white' : '#fd7e14'}; border-radius: 5px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s;">
                            Order
                        </button>
                    </div>
                `;
            })()}
` : `
            <!-- Select Anode Button -->
            <div style="margin: 15px 0;">
                <button
                    class="anode-name-button"
                    style="background-color: #4CAF50; width: 100%;"
                    onclick="window.__openAnodeModalForCard('${key}', '${anode.location}', '${anode.position}')">
                    ➕ Select Anode Model
                </button>
            </div>
`}
        </div>
    `).join('');

    // Initialize active labels and button colors for all cards
    entries.forEach(([key, anode]) => {
        // Set active label
        const sliderValue = getConditionSliderValue(anode.condition);
        const labelsContainer = document.getElementById(`slider-labels-${key}`);
        if (labelsContainer) {
            const activeLabel = labelsContainer.querySelector(`span[data-value="${sliderValue}"]`);
            if (activeLabel) {
                activeLabel.classList.add('active');
            }
        }
    });

    // Update historical conditions if available
    if (window.historicalAnodeConditions) {
        updateHistoricalAnodeConditions();
    }
}

/**
 * Get the slider value (0-10) from condition percent
 */
function getConditionSliderValue(condition) {
    if (condition === undefined || condition === null) return 0;
    const percents = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];
    const index = percents.findIndex(p => p === condition);
    return index >= 0 ? index : 0;
}

/**
 * Format condition value for display
 */
function formatConditionValue(condition) {
    if (condition === undefined || condition === null) return '100%';
    if (condition === 0) return 'Missing';
    return `${condition}%`;
}

/**
 * Get status label for display (simple text)
 */
function getStatusLabel(status) {
    if (status === 'replaced') return 'Replaced';
    if (status === 'retrieve') return 'Retrieve';
    if (status === 'order') return 'Order';
    return 'Inspected';
}

/**
 * Update anode condition and status displays
 */
function updateAnodeDisplays(key) {
    const anode = window.anodeAssignments[key];
    if (!anode) return;

    // Update condition display
    const conditionDisplay = document.getElementById(`condition-display-${key}`);
    if (conditionDisplay) {
        conditionDisplay.textContent = formatConditionValue(anode.condition);
    }

    // Update "This service" current value
    const currentValueDisplay = document.getElementById(`anode-current-${key}`);
    if (currentValueDisplay) {
        currentValueDisplay.textContent = formatConditionValue(anode.condition);
    }

    // Update status display
    const statusDisplay = document.getElementById(`status-display-${key}`);
    if (statusDisplay) {
        statusDisplay.textContent = getStatusLabel(anode.status);
    }

    // Update card title to show anode name
    const card = document.getElementById(`anode-card-${key}`);
    if (card) {
        const title = card.querySelector('.anode-assignment-title');
        if (title) {
            const location = anode.location.charAt(0).toUpperCase() + anode.location.slice(1);
            title.textContent = `${location} - ${anode.position}${anode.anode_name ? `: ${anode.anode_name}` : ''}`;
        }
    }

    // Update status button active states
    updateStatusButtonStates(key);
}

/**
 * Update status button active states
 */
function updateStatusButtonStates(key) {
    const anode = window.anodeAssignments[key];
    if (!anode) return;

    const card = document.getElementById(`anode-card-${key}`);
    if (!card) return;

    const buttons = card.querySelectorAll('.anode-status-btn');
    buttons.forEach(button => {
        const isReplaced = button.textContent.includes('Replaced') && anode.status === 'replaced';
        const isRetrieve = button.textContent.includes('Retrieve') && anode.status === 'retrieve';
        const isOrder = button.textContent.includes('Order') && anode.status === 'order';
        const isActive = isReplaced || isRetrieve || isOrder;

        if (isActive) {
            button.classList.add('active');
            // Set active state styles
            if (isReplaced) {
                button.style.background = '#00b894';
                button.style.color = 'white';
            } else if (isRetrieve) {
                button.style.background = '#3498db';
                button.style.color = 'white';
            } else if (isOrder) {
                button.style.background = '#fd7e14';
                button.style.color = 'white';
            }
        } else {
            button.classList.remove('active');
            // Set inactive state styles
            if (button.textContent.includes('Replaced')) {
                button.style.background = 'white';
                button.style.color = '#00b894';
            } else if (button.textContent.includes('Retrieve')) {
                button.style.background = 'white';
                button.style.color = '#3498db';
            } else if (button.textContent.includes('Order')) {
                button.style.background = 'white';
                button.style.color = '#fd7e14';
            }
        }
    });
}

/**
 * Update anode condition from slider
 */
export function updateAnodeCondition(key, value) {
    const percents = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];

    // Update active label highlighting
    const labelsContainer = document.getElementById(`slider-labels-${key}`);
    if (labelsContainer) {
        const labels = labelsContainer.querySelectorAll('span');
        labels.forEach(label => {
            label.classList.remove('active');
        });
        const activeLabel = labelsContainer.querySelector(`span[data-value="${value}"]`);
        if (activeLabel) {
            activeLabel.classList.add('active');
        }
    }

    if (window.anodeAssignments[key]) {
        window.anodeAssignments[key].condition = percents[value];

        // Mark as changed for unsaved changes tracking
        if (!window.anodeChangesTracking) window.anodeChangesTracking = {};
        window.anodeChangesTracking[key] = true;

        // Update displays
        updateAnodeDisplays(key);

        // Trigger charge summary update
        if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
            window.updateChargeSummary();
        }
        if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
            window.updateWizardPricing();
        }
    }
}

/**
 * Update anode status (with toggle support)
 */
export function updateAnodeStatus(key, status) {
    if (window.anodeAssignments[key]) {
        // Toggle: if clicking the same status, deselect it
        if (window.anodeAssignments[key].status === status) {
            window.anodeAssignments[key].status = null;
        } else {
            window.anodeAssignments[key].status = status;
        }

        // Mark as changed for unsaved changes tracking
        if (!window.anodeChangesTracking) window.anodeChangesTracking = {};
        window.anodeChangesTracking[key] = true;

        // Update displays
        updateAnodeDisplays(key);

        // Trigger charge summary update
        if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
            window.updateChargeSummary();
        }
        if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
            window.updateWizardPricing();
        }
    }
}


/**
 * Open anode catalog modal for a specific assignment card
 */
export function openAnodeModalForCard(key, location, position) {
    openAnodeCatalogModal(location, position, (anodeData) => {
        // Update assignment with selected anode
        if (window.anodeAssignments[key]) {
            window.anodeAssignments[key].anode_id = anodeData.anode_id;
            window.anodeAssignments[key].anode_name = anodeData.anode_name;
            window.anodeAssignments[key].anode_price = anodeData.anode_price;

            // Mark as changed for unsaved changes tracking
            if (!window.anodeChangesTracking) window.anodeChangesTracking = {};
            window.anodeChangesTracking[key] = true;

            // Refresh the card display
            updateAnodeAssignmentsList();

            // Trigger charge summary update
            if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
                window.updateChargeSummary();
            }
            if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
                window.updateWizardPricing();
            }
        }
    });
}


/**
 * Remove an anode from the current charge (session only)
 * Does NOT delete from boat_anodes table - anode will reappear on next load
 */
export function removeAnodeFromCharge(key) {
    if (confirm('Remove this anode from the current charge?\n\nThis will NOT delete it from the boat\'s saved anodes - it will reappear next time you load this boat.')) {
        delete window.anodeAssignments[key];
        updateAnodeAssignmentsList();
        updateLocationCounts();

        // Trigger charge summary update
        if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
            window.updateChargeSummary();
        }
        if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
            window.updateWizardPricing();
        }

        console.log('✅ Anode removed from charge (session only):', key);
    }
}

/**
 * Delete an anode permanently from the boat_anodes table
 * This removes it from the boat's saved anode configuration
 */
export async function deleteAnodePermanently(key, location, position) {
    const boatId = window.selectedBoat?.id || window.selectedBoat?.boat_id;

    if (!boatId) {
        alert('Error: No boat selected');
        return;
    }

    const anode = window.anodeAssignments[key];
    const anodeName = anode?.anode_name || 'this anode';

    const confirmMessage = `DELETE "${anodeName}" permanently?\n\n` +
        `Location: ${location}\n` +
        `Position: ${position}\n\n` +
        `This will remove it from the boat's saved anode configuration.\n` +
        `This action cannot be undone.`;

    if (!confirm(confirmMessage)) {
        return;
    }

    try {
        // Soft delete from boat_anodes table (set is_active = false)
        const { error } = await window.supabaseClient
            .from('boat_anodes')
            .update({ is_active: false, updated_at: new Date().toISOString() })
            .eq('boat_id', boatId)
            .eq('location', location.toLowerCase())
            .eq('position', position.toLowerCase());

        if (error) {
            console.error('Error deleting anode from database:', error);
            alert(`Failed to delete anode: ${error.message}`);
            return;
        }

        // Remove from session
        delete window.anodeAssignments[key];
        updateAnodeAssignmentsList();
        updateLocationCounts();

        // Update boat card anodes display
        if (window.refreshAnodesDisplay && typeof window.refreshAnodesDisplay === 'function') {
            window.refreshAnodesDisplay();
        }

        // Trigger charge summary update
        if (window.updateChargeSummary && typeof window.updateChargeSummary === 'function') {
            window.updateChargeSummary();
        }
        if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
            window.updateWizardPricing();
        }

        console.log('✅ Anode deleted permanently:', { boatId, location, position });

    } catch (err) {
        console.error('Unexpected error deleting anode:', err);
        alert('An unexpected error occurred while deleting the anode');
    }
}

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use removeAnodeFromCharge instead
 */
export function removeAnodeAssignment(key) {
    removeAnodeFromCharge(key);
}

/**
 * Update location count badges
 */
export function updateLocationCounts() {
    // Count anodes per location
    const counts = {};
    Object.values(window.anodeAssignments).forEach(anode => {
        counts[anode.location] = (counts[anode.location] || 0) + 1;
    });

    // Update all count badges
    ['shaft', 'prop', 'strut', 'hull', 'transom', 'rudder', 'trimtab', 'keel', 'thruster', 'saildrive'].forEach(location => {
        const countElem = document.getElementById(`count-${location}`);
        if (countElem) {
            countElem.textContent = counts[location] || 0;
        }

        // Update button active state
        const btn = document.querySelector(`[data-location="${location}"]`);
        if (btn) {
            if (counts[location] > 0) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        }
    });
}

/**
 * Update historical anode conditions display
 * Matches historical conditions from window.historicalAnodeConditions to current assignments
 */
export function updateHistoricalAnodeConditions() {
    if (!window.historicalAnodeConditions || window.historicalAnodeConditions.length === 0) {
        console.log('No historical anode conditions to display');
        return;
    }

    const entries = Object.entries(window.anodeAssignments);
    entries.forEach(([key, anode]) => {
        // Find matching historical condition by location and position
        const historical = window.historicalAnodeConditions.find(h =>
            h.location === anode.location && h.position === anode.position
        );

        if (historical && historical.condition_percent !== undefined && historical.condition_percent !== null) {
            const historicalElem = document.getElementById(`anode-historical-${key}`);
            if (historicalElem) {
                const conditionText = historical.condition_percent === 0 ? 'Missing' : `${historical.condition_percent}%`;
                historicalElem.innerHTML = `Last service: <span style="font-weight: 600;">${conditionText}</span>`;
            }
        }
    });
}

// Expose functions to window for onclick handlers
// (These will be removed when we refactor to use proper event listeners)
window.__updateAnodeAssignmentsList = updateAnodeAssignmentsList;
window.__updateAnodeCondition = updateAnodeCondition;
window.__updateAnodeStatus = updateAnodeStatus;
window.__openAnodeModalForCard = openAnodeModalForCard;
window.__removeAnodeAssignment = removeAnodeAssignment;
window.__removeAnodeFromCharge = removeAnodeFromCharge;
window.__deleteAnodePermanently = deleteAnodePermanently;
window.__updateLocationCounts = updateLocationCounts;

// Also expose via window for backwards compatibility
window.updateAnodeAssignmentsList = updateAnodeAssignmentsList;
window.updateLocationCounts = updateLocationCounts;
window.updateHistoricalAnodeConditions = updateHistoricalAnodeConditions;
window.fetchAnodeInventoryStatus = fetchAnodeInventoryStatus;
window.removeAnodeFromCharge = removeAnodeFromCharge;
window.deleteAnodePermanently = deleteAnodePermanently;
